import { ExternalLink, Info, ListChecks, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Pager } from '@/components/common/Pager'
import { EmptyState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useDeleteFeature } from '../../catalogApi'
import type { CatalogModuleView, ModuleFeature, ProjectSummary } from '../../types'
import { toastError } from '../../ui'
import { MODULE_CATEGORY_ICON } from '../../visuals'
import { ProjectIcon } from '../ProjectBits'
import { FeatureForm } from './ModuleFeatures'

type Props = { catalog: CatalogModuleView[]; projects: ProjectSummary[]; canWrite: boolean }

export function FeatureList({ catalog, projects, canWrite }: Props) {
  const { t } = useTranslation()
  const remove = useDeleteFeature()
  const [q, setQ] = useState('')
  const [moduleId, setModuleId] = useState('all')
  const [group, setGroup] = useState('all')
  const [projectId, setProjectId] = useState('all')
  const [dialog, setDialog] = useState<{ open: boolean; module: CatalogModuleView | null; feature: ModuleFeature | null }>({ open: false, module: null, feature: null })
  const projectById = new Map(projects.map((p) => [p.id, p]))

  const rows = useMemo(() => catalog.flatMap((m) => m.features.map((f) => ({ ...f, module: m }))), [catalog])
  const groups = useMemo(() => [...new Set(rows.map((r) => r.group))].sort((a, b) => a.localeCompare(b, 'tr')), [rows])
  const term = q.trim().toLocaleLowerCase('tr')
  const filtered = rows.filter(
    (r) =>
      (moduleId === 'all' || r.module.id === moduleId) &&
      (group === 'all' || r.group === group) &&
      (projectId === 'all' || r.module.usage.some((u) => u.projectId === projectId)) &&
      (!term || `${r.name} ${r.description} ${r.group} ${r.module.name}`.toLocaleLowerCase('tr').includes(term)),
  )
  const empty = catalog.filter((m) => m.features.length === 0)

  const onDelete = async (f: ModuleFeature, m: CatalogModuleView) => {
    if (!(await confirmModal({ title: t('projects.catalog.features.deleteTitle'), description: t('projects.catalog.features.deleteBody', { name: f.name, module: m.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate({ moduleId: m.id, id: f.id }, { onSuccess: () => toast.success(t('projects.catalog.features.deleted')), onError: toastError })
  }

  const { pageRows, pager } = usePagination(filtered, [q, moduleId, group, projectId])
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 rounded-xl border border-primary/15 bg-primary/[0.04] p-4 sm:flex-row sm:items-center">
        <Info className="size-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">{t('projects.catalog.featureList.whatTitle')}</p>
          <p className="text-muted-foreground">{t('projects.catalog.featureList.whatBody')}</p>
        </div>
        {canWrite ? (
          <Button onClick={() => setDialog({ open: true, module: moduleId === 'all' ? null : (catalog.find((m) => m.id === moduleId) ?? null), feature: null })} disabled={!catalog.length}>
            <Plus />
            {t('projects.catalog.features.add')}
          </Button>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-card">
        <div className="flex flex-col gap-2 border-b p-3 lg:flex-row lg:items-center">
          <div className="relative min-w-0 flex-1 lg:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('projects.catalog.featureList.search')} aria-label={t('projects.catalog.featureList.search')} className="pl-8" />
          </div>
          <div className="grid grid-cols-2 gap-2 lg:flex">
            <Select
              className="lg:w-48"
              value={moduleId}
              onChange={setModuleId}
              aria-label={t('projects.catalog.featureList.module')}
              options={[{ value: 'all', label: t('projects.catalog.featureList.allModules') }, ...catalog.map((m) => ({ value: m.id, label: m.name }))]}
            />
            <Select
              className="lg:w-44"
              value={group}
              onChange={setGroup}
              aria-label={t('projects.catalog.features.group')}
              options={[{ value: 'all', label: t('projects.catalog.featureList.allGroups') }, ...groups.map((g) => ({ value: g, label: g }))]}
            />
            <Select
              className="lg:w-44"
              value={projectId}
              onChange={setProjectId}
              aria-label={t('projects.catalog.project')}
              options={[{ value: 'all', label: t('projects.catalog.allProjects') }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
            />
          </div>
          <span className="text-xs text-muted-foreground lg:ml-auto">{t('projects.catalog.featureList.count', { count: filtered.length, total: rows.length })}</span>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={ListChecks} title={rows.length ? t('projects.catalog.noMatch') : t('projects.catalog.featureList.empty')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH className="min-w-64">{t('projects.catalog.featureList.feature')}</TH>
                  <TH>{t('projects.catalog.features.group')}</TH>
                  <TH>{t('projects.catalog.featureList.module')}</TH>
                  <TH>{t('projects.catalog.featureList.products')}</TH>
                  <TH className="w-px whitespace-nowrap text-right">{t('projects.catalog.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {pageRows.map((r) => {
                  const Icon = MODULE_CATEGORY_ICON[r.module.category]
                  return (
                    <TR key={r.id} className={cn(!r.module.active && 'text-muted-foreground')}>
                      <TD>
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-muted-foreground">{r.description}</p>
                      </TD>
                      <TD className="whitespace-nowrap text-muted-foreground">{r.group}</TD>
                      <TD>
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <Icon className="size-3.5 text-muted-foreground" aria-hidden />
                          {r.module.name}
                        </span>
                      </TD>
                      <TD>
                        {r.module.usage.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {r.module.usage.map((u) => {
                              const p = projectById.get(u.projectId)
                              return p ? (
                                <span key={u.projectId} className="inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2 text-xs" title={t(`projects.requirement.${u.requirement}`)}>
                                  <ProjectIcon icon={p.icon} color={p.color} className="size-4 rounded shadow-none" />
                                  {p.name}
                                  <span className={cn('size-1.5 rounded-full', u.requirement === 'required' ? 'bg-success' : 'bg-info')} aria-label={t(`projects.requirement.${u.requirement}`)} />
                                </span>
                              ) : null
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('projects.catalog.unused')}</span>
                        )}
                      </TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          <Tooltip content={t('projects.catalog.featureList.openModule')} side="top">
                            <Button size="icon" variant="outline" className="size-8" asChild>
                              <Link to={`/projects/catalog/${r.module.id}`} aria-label={t('projects.catalog.featureList.openModuleFor', { name: r.module.name })}>
                                <ExternalLink />
                              </Link>
                            </Button>
                          </Tooltip>
                          {canWrite ? (
                            <>
                              <Tooltip content={t('common.edit')} side="top">
                                <Button size="icon" variant="outline" className="size-8" aria-label={t('projects.catalog.featureList.editFor', { name: r.name })} onClick={() => setDialog({ open: true, module: r.module, feature: r })}>
                                  <Pencil />
                                </Button>
                              </Tooltip>
                              <Tooltip content={t('common.delete')} side="top">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="size-8 text-destructive hover:text-destructive"
                                  aria-label={t('projects.catalog.featureList.deleteFor', { name: r.name })}
                                  disabled={remove.isPending}
                                  onClick={() => void onDelete(r, r.module)}
                                >
                                  <Trash2 />
                                </Button>
                              </Tooltip>
                            </>
                          ) : null}
                        </div>
                      </TD>
                    </TR>
                  )
                })}
              </TBody>
            </Table>
            <Pager {...pager} />
          </>
        )}

        {empty.length ? (
          <div className="flex flex-wrap items-center gap-2 border-t px-4 py-2 text-xs">
            <span className="text-warning-strong">{t('projects.catalog.featureList.noFeatures', { names: empty.map((m) => m.name).join(', ') })}</span>
            {canWrite ? (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDialog({ open: true, module: empty[0] ?? null, feature: null })}>
                <Plus className="size-3.5" />
                {t('projects.catalog.featureList.fillFirst', { name: empty[0]?.name })}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {dialog.open ? (
        <FeatureForm
          key={`${dialog.module?.id ?? 'any'}-${dialog.feature?.id ?? 'new'}`}
          module={dialog.module}
          catalog={catalog}
          feature={dialog.feature}
          onDone={() => setDialog({ open: false, module: null, feature: null })}
        />
      ) : null}
    </div>
  )
}
